import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Sun, Moon, Users, Users2, Briefcase, Activity, ArrowRight, Compass, Lock } from "lucide-react";
import { getRoleFromToken } from "../../api";
import SideBarNavGestaoOrcamentaria from "../../componets/SideBarNavGestaoOrcamentaria";
import { ROLES_MAO_DE_OBRA } from "../../constants/maoDeObra";
import { ROLES_ALOCACAO_PESSOAL } from "../../constants/alocacaoPessoal";
import { ROLES_CUSTOS_GERAIS_PROJETO } from "../../constants/custosGeraisProjeto";
import { ROLES_VISAO_EXECUTIVA } from "../../constants/visaoExecutiva";

// Paleta padrão da plataforma (mesma usada nas demais telas de Gestão Orçamentária)
const palette = (isLight) => ({
  bg: isLight ? "#F8F7FC" : "#0F0A1E",
  card: isLight ? "#FFFFFF" : "#1A1030",
  border: isLight ? "#E5E0F0" : "#2E2050",
  text: isLight ? "#1A1030" : "#EDE9FA",
  muted: isLight ? "#7C6FA0" : "#9585C0",
  brand: "#7C3AED",
  bgActive: isLight ? "#F1E9FE" : "#241A3D",
});

// Passo a passo do fluxo de Custos (Operação): cadastra pessoal → aloca esse pessoal aos
// projetos → lança os demais custos por projeto → acompanha tudo consolidado. A ordem aqui
// é a ordem recomendada de uso, não necessariamente a ordem do menu lateral.
const PASSOS = [
  {
    numero: 1,
    icon: Users,
    titulo: "Custo Mão de Obra",
    path: "/gestao-orcamentaria/custo-mao-de-obra",
    roles: ROLES_MAO_DE_OBRA,
    resumo: "Cadastre os colaboradores da Operação e a composição de custo de cada um — salário, encargos e benefícios.",
    pontos: [
      "\"Nova linha\" cadastra um colaborador — é por aqui que entra uma contratação nova.",
      "Reajuste 1 e Reajuste 2 registram a evolução do colaborador ao longo do ano (mudança de salário, agrupamento, enquadramento ou regime), cada um com seu mês de vigência.",
      "Encargos, benefícios e a projeção mensal de custo são calculados automaticamente a partir desses dados.",
    ],
  },
  {
    numero: 2,
    icon: Users2,
    titulo: "Alocação de Pessoal",
    path: "/gestao-orcamentaria/alocacao-pessoal",
    roles: ROLES_ALOCACAO_PESSOAL,
    resumo: "Distribua, mês a mês, o percentual de tempo de cada colaborador cadastrado no passo anterior entre os projetos.",
    pontos: [
      "Clique no colaborador para abrir a grade com todos os projetos × os 12 meses de uma vez.",
      "A soma do % alocado em cada mês não pode passar de 100%.",
      "Essa alocação alimenta o tipo de lançamento \"Alocação por Projeto\" em Custos Gerais por Projeto (próximo passo).",
    ],
  },
  {
    numero: 3,
    icon: Briefcase,
    titulo: "Custos Gerais por Projeto",
    path: "/gestao-orcamentaria/custos-gerais-projeto",
    roles: ROLES_CUSTOS_GERAIS_PROJETO,
    resumo: "Lance, projeto a projeto, os demais custos (viagens, softwares, serviços...) pelos botões de categoria.",
    pontos: [
      "Abra o projeto e clique no botão da categoria (ex: Despesas de Viagens) para escolher a subcategoria e lançar o item.",
      "Despesas de Viagens tem o atalho \"Lançar Viagem\", que usa o ticket médio travado do projeto.",
      "Cada item pode ter mais de um período de vigência, quando o valor muda ao longo do ano.",
    ],
  },
  {
    numero: 4,
    icon: Activity,
    titulo: "Visão Executiva",
    path: "/gestao-orcamentaria/visao-executiva",
    roles: ROLES_VISAO_EXECUTIVA,
    resumo: "Acompanhe o resultado dos 3 passos anteriores: saúde da alocação, ranking de custo por projeto e ociosidade acumulada.",
    pontos: [
      "Mostra quem está sub ou sobre-alocado em cada mês.",
      "Ranking de custo de pessoal por projeto, cruzando Custo Mão de Obra × Alocação de Pessoal.",
      "Ociosidade acumulada — tempo de colaboradores não alocado a nenhum projeto.",
    ],
  },
];

export default function BoasVindasCustosView() {
  const [isLight, setIsLight] = useState(true);
  const C = palette(isLight);
  const navigate = useNavigate();
  const role = getRoleFromToken();

  return (
    <div style={{ display: "flex", height: "100vh", overflow: "hidden", background: C.bg, fontFamily: "'DM Sans', sans-serif", color: C.text }}>
      <SideBarNavGestaoOrcamentaria isLight={isLight} />

      <div style={{ flex: 1, display: "flex", flexDirection: "column", overflow: "auto" }}>
        <header style={{
          background: "linear-gradient(90deg, #4c1d95 0%, #9d174d 100%)", color: "#fff", padding: "14px 24px",
          display: "flex", justifyContent: "space-between", alignItems: "center", boxShadow: "0 2px 8px rgba(0,0,0,0.25)",
        }}>
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <span style={{ fontSize: 22, fontWeight: 800 }}>YOUX</span>
            <span style={{ fontSize: 18, fontWeight: 300 }}>GROUP</span>
            <span style={{ fontSize: 13, fontWeight: 500, opacity: 0.8, marginLeft: 8 }}>· Boas-vindas · Custos</span>
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <button onClick={() => setIsLight((l) => !l)} style={{
              display: "flex", alignItems: "center", gap: 6, background: "rgba(255,255,255,0.12)",
              border: "1px solid rgba(255,255,255,0.25)", borderRadius: 8, padding: "7px 12px",
              cursor: "pointer", color: "#fff", fontSize: 12, fontWeight: 600,
            }}>
              {isLight ? <Sun size={14} /> : <Moon size={14} />} {isLight ? "Tema Claro" : "Tema Escuro"}
            </button>
            <button onClick={() => navigate("/inicio")} style={{
              display: "flex", alignItems: "center", gap: 6, background: "rgba(255,255,255,0.12)",
              border: "1px solid rgba(255,255,255,0.25)", borderRadius: 8, padding: "7px 14px",
              cursor: "pointer", color: "#fff", fontSize: 13, fontWeight: 600,
            }}>
              Menu Principal
            </button>
          </div>
        </header>

        <main style={{ padding: 24, display: "flex", flexDirection: "column", gap: 24, width: "100%", maxWidth: 860, margin: "0 auto" }}>
          {/* Hero */}
          <section style={{
            display: "flex", alignItems: "flex-start", gap: 16,
            background: C.card, border: `1px solid ${C.border}`, borderRadius: 16, padding: "22px 24px",
          }}>
            <div style={{
              display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0,
              width: 46, height: 46, borderRadius: 12, background: `${C.brand}1F`, color: C.brand,
            }}>
              <Compass size={24} />
            </div>
            <div>
              <h1 style={{ margin: "0 0 6px", fontSize: 18, fontWeight: 800, color: C.text }}>Bem-vindo à Gestão de Custos (Operação)</h1>
              <p style={{ margin: 0, fontSize: 13, color: C.muted, lineHeight: 1.6 }}>
                Este é o passo a passo recomendado para planejar e acompanhar o custo dos projetos da Operação:
                cadastre o pessoal, aloque esse pessoal aos projetos, lance os demais custos por projeto e
                acompanhe tudo consolidado na Visão Executiva. Siga os 4 passos abaixo na ordem — cada um usa
                dados do anterior.
              </p>
            </div>
          </section>

          {/* Passo a passo */}
          <section style={{ display: "flex", flexDirection: "column" }}>
            {PASSOS.map((passo, idx) => {
              const Icon = passo.icon;
              const ultimo = idx === PASSOS.length - 1;
              const temAcesso = passo.roles.includes(role);
              return (
                <div key={passo.numero} style={{ display: "flex", gap: 18 }}>
                  <div style={{ display: "flex", flexDirection: "column", alignItems: "center", width: 36, flexShrink: 0 }}>
                    <div style={{
                      display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0,
                      width: 36, height: 36, borderRadius: "50%", background: C.brand, color: "#fff",
                      fontSize: 15, fontWeight: 800,
                    }}>
                      {passo.numero}
                    </div>
                    {!ultimo && <div style={{ flex: 1, width: 2, background: C.border, marginTop: 6, marginBottom: 6, minHeight: 24 }} />}
                  </div>

                  <div style={{ flex: 1, paddingBottom: ultimo ? 0 : 22, minWidth: 0 }}>
                    <div style={{ background: C.card, border: `1px solid ${C.border}`, borderRadius: 14, padding: "18px 20px", display: "flex", flexDirection: "column", gap: 10 }}>
                      <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                        <Icon size={18} color={C.brand} />
                        <h2 style={{ margin: 0, fontSize: 15, fontWeight: 700, color: C.text }}>{passo.titulo}</h2>
                      </div>
                      <p style={{ margin: 0, fontSize: 13, color: C.text, lineHeight: 1.5 }}>{passo.resumo}</p>
                      <ul style={{ margin: 0, padding: "0 0 0 18px", display: "flex", flexDirection: "column", gap: 4 }}>
                        {passo.pontos.map((ponto, i) => (
                          <li key={i} style={{ fontSize: 12.5, color: C.muted, lineHeight: 1.5 }}>{ponto}</li>
                        ))}
                      </ul>
                      {temAcesso ? (
                        <button
                          onClick={() => navigate(passo.path)}
                          style={{
                            alignSelf: "flex-start", display: "flex", alignItems: "center", gap: 6,
                            background: "transparent", border: `1px solid ${C.brand}`, borderRadius: 8,
                            padding: "8px 14px", color: C.brand, fontSize: 12.5, fontWeight: 700,
                            cursor: "pointer", fontFamily: "inherit", marginTop: 4,
                          }}
                        >
                          Ir para {passo.titulo} <ArrowRight size={13} />
                        </button>
                      ) : (
                        <span style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 11.5, color: C.muted, marginTop: 4 }}>
                          <Lock size={12} /> Sem acesso a esta tela com o seu perfil atual
                        </span>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </section>
        </main>
      </div>
    </div>
  );
}
